// The value picker's selection logic (no React): what a rule value becomes
// when a value, a group, "select all shown" or "clear" is clicked, and which
// values a search shows. Values are `[code, label, count]` as master-values
// returns them. A rule value that is "every value but these" stays one when a
// value is ticked or unticked, so values the master gains later keep their
// answer; Any becomes an explicit list only when the person narrows it.

import type { UdaRuleValue } from '../types'
import { ANY, accepts, canonValue, isExcept } from '../rules'
import { PICKER_LOCAL_MAX } from '../limits'

export type PickValue = [string, string, number | null | undefined]

/** Values matching a search (label or code, any case). */
export function filterValues(values: PickValue[], q: string): PickValue[] {
  const needle = String(q || '').trim().toLowerCase()
  if (!needle) return values
  return values.filter(([code, label]) => String(label || '').toLowerCase().includes(needle)
    || String(code).toLowerCase().includes(needle))
}

/** Whether a picker over `total` values searches on the server. */
export function serverSearch(total: number | null | undefined): boolean {
  return typeof total === 'number' && total > PICKER_LOCAL_MAX
}

export function isPicked(value: UdaRuleValue | undefined, code: string): boolean {
  return accepts(canonValue(value), code)
}

/** Tick / untick one value. From Any, the list starts as every known value. */
export function toggleValue(value: UdaRuleValue | undefined, code: string, allCodes: string[]): UdaRuleValue {
  const v = canonValue(value)
  if (isExcept(v)) {
    const excl = v.any_except.includes(code)
      ? v.any_except.filter(c => c !== code) : [...v.any_except, code]
    return excl.length ? { any_except: excl } : ANY
  }
  const list = v === ANY ? [...allCodes] : [...(v as string[])]
  return list.includes(code) ? list.filter(c => c !== code) : [...list, code]
}

/** Add every shown value (prototype `pkall`). */
export function selectShown(value: UdaRuleValue | undefined, shown: string[]): UdaRuleValue {
  const v = canonValue(value)
  if (v === ANY) return ANY
  if (isExcept(v)) {
    const excl = v.any_except.filter(c => !shown.includes(c))
    return excl.length ? { any_except: excl } : ANY
  }
  const list = [...(v as string[])]
  for (const c of shown) if (!list.includes(c)) list.push(c)
  return list
}

/** A whole group: on when every member is picked, else every member added
 *  (prototype `pkgroup`). */
export function toggleGroup(value: UdaRuleValue | undefined, members: string[]): UdaRuleValue {
  const v = canonValue(value)
  const allIn = members.length > 0 && members.every(c => accepts(v, c))
  if (isExcept(v)) {
    const excl = allIn ? [...new Set([...v.any_except, ...members])] : v.any_except.filter(c => !members.includes(c))
    return excl.length ? { any_except: excl } : ANY
  }
  const list = v === ANY ? [] : [...(v as string[])]
  if (allIn && v !== ANY) return list.filter(c => !members.includes(c))
  for (const c of members) if (!list.includes(c)) list.push(c)
  return list
}

export function groupPicked(value: UdaRuleValue | undefined, members: string[]): boolean {
  const v = canonValue(value)
  return v !== ANY && members.length > 0 && members.every(c => accepts(v, c))
}

/** "All" / "12 of 140" / "All but 2". */
export function pickedText(value: UdaRuleValue | undefined, total: number): string {
  const v = canonValue(value)
  if (v === ANY) return 'All selected'
  if (isExcept(v)) return `All but ${v.any_except.length} selected`
  return `${(v as string[]).length} of ${total} selected`
}

/** What Done does with an empty "only these" list: it becomes Any (the
 *  prototype's "Nothing was picked, so it is set to Any"). */
export function closeValue(value: UdaRuleValue | undefined): { value: UdaRuleValue; becameAny: boolean } {
  const v = canonValue(value)
  if (Array.isArray(v) && v.length === 0) return { value: ANY, becameAny: true }
  return { value: v, becameAny: false }
}
